import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {
  ServerAuthenticationStrategiesEnum,
  ServerOAuth2AuthenticationConfig,
} from '../../../../models/api/serverModel';
import {
  StyledParameterLineDiv,
  StyledParameterTitleLineDiv,
} from '../../../rules/ruleEditor/expressions/parameterLine';

/*
 * Props.
 */

interface ServerAuthenticationOAuth2InputsProps {
  clientId: string;
  clientSecret: string;
  scope: string;
  tokenUrl: string;
  authorizationUrl: string;
  onUpdate: HandlerOf<ServerOAuth2AuthenticationConfig>;
  isReadOnly: boolean;
}

/*
 * Component.
 */

// @TODO: Remove requestUrl.
export const ServerAuthenticationOAuth2Inputs: FC<ServerAuthenticationOAuth2InputsProps> = (props) => {
  const {clientId, clientSecret, scope, tokenUrl, authorizationUrl, onUpdate, isReadOnly} = props;

  const onUpdateClientId = useCallback(
    (newClientId: string) =>
      onUpdate({
        strategy: ServerAuthenticationStrategiesEnum.OAUTH2,
        clientId: newClientId,
        clientSecret,
        scope,
        tokenUrl,
        authorizationUrl,
      }),
    [clientSecret, onUpdate, scope, tokenUrl, authorizationUrl],
  );

  const onUpdateClientSecret = useCallback(
    (newClientSecret: string) =>
      onUpdate({
        strategy: ServerAuthenticationStrategiesEnum.OAUTH2,
        clientId,
        clientSecret: newClientSecret,
        scope,
        tokenUrl,
        authorizationUrl,
      }),
    [clientId, onUpdate, scope, tokenUrl, authorizationUrl],
  );

  const onUpdateScope = useCallback(
    (newScope: string) =>
      onUpdate({
        strategy: ServerAuthenticationStrategiesEnum.OAUTH2,
        clientId,
        clientSecret,
        scope: newScope,
        tokenUrl,
        authorizationUrl,
      }),
    [clientId, clientSecret, onUpdate, tokenUrl, authorizationUrl],
  );

  const onUpdateTokenUrl = useCallback(
    (newTokenUrl: string) =>
      onUpdate({
        strategy: ServerAuthenticationStrategiesEnum.OAUTH2,
        clientId,
        clientSecret,
        scope,
        tokenUrl: newTokenUrl,
        authorizationUrl,
      }),
    [clientId, clientSecret, onUpdate, scope, authorizationUrl],
  );

  const onUpdateAuthorizationUrl = useCallback(
    (newAuthorizationUrl: string) =>
      onUpdate({
        strategy: ServerAuthenticationStrategiesEnum.OAUTH2,
        clientId,
        clientSecret,
        scope,
        tokenUrl,
        authorizationUrl: newAuthorizationUrl,
      }),
    [clientId, clientSecret, onUpdate, scope],
  );

  return (
    <>
      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="server-editor-client-id-label">Client ID</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <TextBox
          value={clientId}
          onChange={onUpdateClientId}
          size={VisualSizesEnum.MEDIUM}
          noMargin
          isDisabled={isReadOnly}
        />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="server-editor-client-secret-label">Client secret</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <TextBox
          value={clientSecret}
          onChange={onUpdateClientSecret}
          size={VisualSizesEnum.MEDIUM}
          noMargin
          isDisabled={isReadOnly}
        />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="server-editor-scope-label">Scope</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <TextBox
          value={scope}
          onChange={onUpdateScope}
          size={VisualSizesEnum.MEDIUM}
          noMargin
          isDisabled={isReadOnly}
        />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="server-editor-token-url-label">Token URL</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <TextBox
          value={tokenUrl}
          onChange={onUpdateTokenUrl}
          size={VisualSizesEnum.MEDIUM}
          noMargin
          isDisabled={isReadOnly}
        />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="server-editor-authorization-url-label">Authorization URL</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <TextBox
          value={authorizationUrl}
          onChange={onUpdateAuthorizationUrl}
          size={VisualSizesEnum.MEDIUM}
          noMargin
          isDisabled={isReadOnly}
        />
      </StyledParameterLineDiv>
    </>
  );
};
